/**
 * Category Manager Modal Component
 * For managing custom photo categories
 */

import React, { useState, useEffect } from 'react';
import type { PhotoCategory, CategoryIcon } from '../../../../shared/types/project';
import { DEFAULT_PHOTO_CATEGORIES, CATEGORY_ICONS } from '../../../../shared/types/project';
import '../../../components/modals/ProjectModal.css';

interface CategoryManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  customCategories: PhotoCategory[];
  onSave: (categories: PhotoCategory[]) => void;
  photosUsingCategory: (categoryId: string) => number;
}

const PRESET_COLORS = [
  '#4F7CFF', '#ff4444', '#00cc88', '#ffaa00',
  '#9b59b6', '#3498db', '#e91e63', '#607d8b',
  '#ff5722', '#795548', '#2196f3', '#4caf50',
];

const ICON_LABELS: Record<CategoryIcon, string> = {
  camera: 'Camera',
  warning: 'Warning',
  check: 'Checkmark',
  flag: 'Flag',
  pin: 'Pin',
  star: 'Star',
  info: 'Info',
  tool: 'Tool',
  building: 'Building',
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  hvac: 'HVAC',
  structural: 'Structural',
  fire: 'Fire',
  exterior: 'Exterior',
  interior: 'Interior',
  drone: 'Drone',
};

interface EditingCategory {
  id: string;
  name: string;
  color: string;
  icon: CategoryIcon;
  isNew?: boolean;
}

export function CategoryManagerModal({
  isOpen,
  onClose,
  customCategories,
  onSave,
  photosUsingCategory,
}: CategoryManagerModalProps) {
  const [categories, setCategories] = useState<PhotoCategory[]>([]);
  const [editing, setEditing] = useState<EditingCategory | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Initialize with custom categories when modal opens
  useEffect(() => {
    if (isOpen) {
      setCategories([...customCategories]);
      setEditing(null);
      setDeleteConfirm(null);
    }
  }, [isOpen, customCategories]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (editing) {
          setEditing(null);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, editing]);

  const handleAddNew = () => {
    setEditing({
      id: `custom-${Date.now()}`,
      name: '',
      color: PRESET_COLORS[categories.length % PRESET_COLORS.length],
      icon: 'pin',
      isNew: true,
    });
  };

  const handleEdit = (category: PhotoCategory) => {
    setEditing({ ...category });
  };

  const handleSaveEdit = () => {
    if (!editing || !editing.name.trim()) return;

    const updated: PhotoCategory = {
      id: editing.id,
      name: editing.name.trim(),
      color: editing.color,
      icon: editing.icon,
    };

    if (editing.isNew) {
      setCategories([...categories, updated]);
    } else {
      setCategories(categories.map(c => c.id === updated.id ? updated : c));
    }
    setEditing(null);
  };

  const handleDelete = (categoryId: string) => {
    const photoCount = photosUsingCategory(categoryId);
    if (photoCount > 0) {
      setDeleteConfirm(categoryId);
    } else {
      setCategories(categories.filter(c => c.id !== categoryId));
    }
  };

  const confirmDelete = () => {
    if (deleteConfirm) {
      setCategories(categories.filter(c => c.id !== deleteConfirm));
      setDeleteConfirm(null);
    }
  };

  const handleSaveAll = () => {
    onSave(categories);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
        <div className="modal__header">
          <h2 className="modal__title">Manage Categories</h2>
          <button className="modal__close" onClick={onClose} title="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="modal__body">
          {/* Default Categories (read-only) */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{
              fontSize: 'var(--font-sm)',
              color: 'var(--text-secondary)',
              marginBottom: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Default Categories
            </h3>
            {DEFAULT_PHOTO_CATEGORIES.map(cat => (
              <div
                key={cat.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 12px',
                  background: 'var(--bg-tertiary)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '8px',
                }}
              >
                <span style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: cat.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <CategoryIconDisplay icon={cat.icon} size={14} />
                </span>
                <span style={{ flex: 1, color: 'var(--text-primary)' }}>{cat.name}</span>
                <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--font-xs)' }}>
                  (default)
                </span>
              </div>
            ))}
          </div>

          {/* Custom Categories */}
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <h3 style={{
                fontSize: 'var(--font-sm)',
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Custom Categories
              </h3>
              <button
                className="button button--primary"
                style={{ padding: '6px 12px', fontSize: 'var(--font-sm)' }}
                onClick={handleAddNew}
                disabled={editing !== null}
              >
                + Add Category
              </button>
            </div>

            {categories.length === 0 && !editing && (
              <p style={{
                color: 'var(--text-tertiary)',
                fontSize: 'var(--font-sm)',
                textAlign: 'center',
                padding: '20px',
              }}>
                No custom categories yet
              </p>
            )}

            {categories.map(cat => (
              <div
                key={cat.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 12px',
                  background: 'var(--bg-tertiary)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '8px',
                }}
              >
                <span style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: cat.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <CategoryIconDisplay icon={cat.icon} size={14} />
                </span>
                <span style={{ flex: 1, color: 'var(--text-primary)' }}>{cat.name}</span>
                <button
                  onClick={() => handleEdit(cat)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    padding: '4px',
                  }}
                  title="Edit"
                  disabled={editing !== null}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDelete(cat.id)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--error)',
                    cursor: 'pointer',
                    padding: '4px',
                  }}
                  title="Delete"
                  disabled={editing !== null}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M8 6V4h8v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" />
                  </svg>
                </button>
              </div>
            ))}

            {/* Edit/Add Form */}
            {editing && (
              <div style={{
                padding: '16px',
                background: 'var(--bg-elevated)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-focus)',
                marginTop: '12px',
              }}>
                <div className="form-group">
                  <label className="label">Name</label>
                  <input
                    type="text"
                    className="input"
                    value={editing.name}
                    onChange={e => setEditing({ ...editing, name: e.target.value })}
                    placeholder="Category name..."
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label className="label">Color</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {PRESET_COLORS.map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setEditing({ ...editing, color })}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: color,
                          border: editing.color === color ? '3px solid white' : '2px solid transparent',
                          cursor: 'pointer',
                          boxShadow: editing.color === color ? '0 0 0 2px var(--accent-primary)' : 'none',
                        }}
                      />
                    ))}
                    <input
                      type="color"
                      value={editing.color}
                      onChange={e => setEditing({ ...editing, color: e.target.value })}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                      title="Custom color"
                    />
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label className="label">Icon</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {CATEGORY_ICONS.map(icon => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => setEditing({ ...editing, icon })}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: 'var(--radius-md)',
                          background: editing.icon === icon ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: editing.icon === icon ? 'white' : 'var(--text-secondary)',
                        }}
                        title={ICON_LABELS[icon]}
                      >
                        <CategoryIconDisplay icon={icon} size={20} />
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button className="button" onClick={() => setEditing(null)}>
                    Cancel
                  </button>
                  <button
                    className="button button--primary"
                    onClick={handleSaveEdit}
                    disabled={!editing.name.trim()}
                  >
                    {editing.isNew ? 'Add' : 'Save'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Delete Confirmation */}
          {deleteConfirm && (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1001,
            }}>
              <div style={{
                background: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-lg)',
                padding: '24px',
                maxWidth: '400px',
              }}>
                <h3 style={{ color: 'var(--text-primary)', marginBottom: '12px' }}>
                  Delete Category?
                </h3>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
                  This category is used by {photosUsingCategory(deleteConfirm)} photo(s).
                  Deleting it will reassign those photos to the "General" category.
                </p>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button className="button" onClick={() => setDeleteConfirm(null)}>
                    Cancel
                  </button>
                  <button
                    className="button"
                    style={{ background: 'var(--error)', color: 'white' }}
                    onClick={confirmDelete}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="modal__actions">
            <button className="button" onClick={onClose}>
              Cancel
            </button>
            <button className="button button--primary" onClick={handleSaveAll}>
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper component to display category icons
function CategoryIconDisplay({ icon, size = 16 }: { icon: CategoryIcon; size?: number }) {
  const iconPaths: Record<CategoryIcon, React.ReactNode> = {
    camera: (
      <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z M12 17a4 4 0 100-8 4 4 0 000 8z" />
    ),
    warning: (
      <path d="M12 2L2 22h20L12 2zM12 16v2m0-6v4" />
    ),
    check: (
      <path d="M20 6L9 17l-5-5" />
    ),
    flag: (
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1zM4 22v-7" />
    ),
    pin: (
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z M12 13a3 3 0 100-6 3 3 0 000 6z" />
    ),
    star: (
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    ),
    info: (
      <><circle cx="12" cy="12" r="9" strokeWidth="3" /><circle cx="12" cy="7.5" r="1.5" fill="currentColor" stroke="none" /><path d="M9.5 10.5h5v1H13v4h1.5v1h-5v-1H11v-4H9.5z" fill="currentColor" stroke="none" /></>
    ),
    tool: (
      <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
    ),
    building: (
      <path d="M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1M5 21V5a2 2 0 012-2h10a2 2 0 012 2v16" />
    ),
    electrical: (
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    ),
    plumbing: (
      <path d="M4 14v-2a4 4 0 014-4h2m10 6v2a4 4 0 01-4 4h-2M14 4l-4 4 4 4M10 20l4-4-4-4" />
    ),
    hvac: (
      <path d="M8 16a4 4 0 01-4-4 4 4 0 014-4m8 8a4 4 0 004-4 4 4 0 00-4-4m-4-4v16m-4-4h8" />
    ),
    structural: (
      <path d="M2 20h20M4 20V10l8-6 8 6v10M9 20v-4h6v4M12 4v2" />
    ),
    fire: (
      <path d="M12 22c4-4 8-7.5 8-12a8 8 0 10-16 0c0 4.5 4 8 8 12zM12 12a2 2 0 100-4 2 2 0 000 4z" />
    ),
    exterior: (
      <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" />
    ),
    interior: (
      <path d="M20 9v11a2 2 0 01-2 2H6a2 2 0 01-2-2V9M9 22V12h6v10M2 10.6L12 2l10 8.6" />
    ),
    drone: (
      <path d="M12 9a3 3 0 100 6 3 3 0 000-6zM9.5 10.5L5 6M14.5 10.5L19 6M9.5 13.5L5 18M14.5 13.5L19 18M5 6a3 3 0 110-1M19 6a3 3 0 010-1M5 18a3 3 0 100 1M19 18a3 3 0 110 1" />
    ),
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {iconPaths[icon]}
    </svg>
  );
}

export { CategoryIconDisplay };
